/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
/**
 * @prop {String} header - Defines the header text shown on top of the progress bar or progress circle.
 * @prop {String} label - Defines the label text shown on top of the progress bar or inside the progress circle.
 * @prop {String} info - Defines the information text displayed below the progress bar/circle.
 * @prop {String} status - Defines the icon shown before the info text. Possible values are `error`, `warning` and `success`.
 * @prop {String} color - Custom color for progress visualization.
 * @prop {Number} value - Default 0. Represents the progress in percentage. Defines the width of the progress bar.
 * @prop {Boolean} indeterminate - Default false. If set to `true`, the progress bar will display a looping animation, and not the exact value.
 * @prop {Boolean} paused - Default false. If set to `true`, the progress bar will change from orange to a neutral color.
 * @prop {Boolean} radial - If set to `true`, the progress bar will be shown in a radial orientation instead of linear.
 * @prop {String} size - If radial is set to true, defines the size of the progress circle (xs: 16px, s: 32px, m: 64px, l: 128px, xl: 256px)
 * @prop {String} type - Defines the icon shown before the info text. Possible values are `error`, `warning` and `success`.
 * @slot functions - Takes Icons and Text Links as children. Should be used for actions such as pause, cancel, resume.
 */
export class SHProgress extends LitElement {
  static get properties() {
    return {
      header: { type: String, reflect: true },
      label: { type: String, reflect: true },
      info: { type: String, reflect: true },
      status: { type: String, reflect: true },
      color: { type: String, reflect: true },
      size: { type: String, reflect: true },
      value: { type: Number, reflect: true },
      indeterminate: { type: Boolean, reflect: true },
      paused: { type: Boolean, reflect: true },
      radial: { type: Boolean, reflect: true },
      // legacy
      type: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
          display: flex;
          flex-direction: column;
        }
        .header-wrapper,
        .footer {
          display: flex;
          align-items: center;
        }
        .info {
          line-height: 1rem;
        }
        :host(:not([radial])) .header,
        :host(:not([radial])) .info {
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .header,
        .label {
          line-height: 1.25rem;
        }
        .label {
          width: auto;
        }
        .header + .label {
          margin-left: 0.5rem;
        }
        :host(:not([header])) .label {
          flex: 1;
          text-align: center;
        }
        .header-wrapper {
          margin-bottom: 0.5rem;
        }
        .footer {
          margin-top: 0.5rem;
          width: 100%;
        }
        .bar-background {
          width: 100%;
          height: 0.5rem;
          border-radius: 0.25rem;
          overflow: hidden;
          position: relative;
          background: rgba(var(--ui-1), var(--opacity-6));
        }
        .bar-value {
          height: 0.5rem;
          background: rgb(var(--ui-2));
          position: absolute;
          max-width: 100%;
        }

        /* radial */
        :host([radial]) {
          align-items: center;
          justify-content: center;
        }
        .radial-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .radial {
          transform: rotate(-90deg);
        }
        circle {
          fill: transparent;
          stroke-width: 0.5rem;
        }
        :host([size='xl']) circle {
          stroke-width: 0.75rem;
        }
        :host([size='xs']) circle {
          stroke-width: 0.25rem;
        }
        :host([size='s']) circle {
          stroke-width: 0.375rem;
        }
        :host([size='l']) circle {
          stroke-width: 0.625rem;
        }
        :host([radial]) .info,
        :host([radial]) .header {
          text-align: center;
        }
        .radial-label {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        :host([radial][size='m']) .radial-label {
          width: 2.5rem;
          line-height: 1rem;
        }
        :host([radial][size='l']) .radial-label {
          font-size: 1.75rem;
          width: 5.5rem;
          line-height: 2rem;
        }
        :host([radial][size='xl']) .radial-label {
          font-size: 3.5rem;
          width: 10.25rem;
          line-height: 3.75rem;
        }
        .radial-wrapper sh-text {
          position: absolute;
          width: 100%;
          text-align: center;
        }
        /* status */
        .status-icon {
          margin-right: 0.5rem;
        }
        .status-icon[icon='cancel'] {
          color: rgb(var(--functional-red));
        }
        .status-icon[icon='error'] {
          color: rgb(var(--functional-yellow));
        }
        .status-icon[icon='check_circle'] {
          color: rgb(var(--functional-green));
        }
        /* functions slot */
        slot[name='functions']::slotted(*) {
          margin-left: 0.5rem;
        }
        /* indeterminate */
        :host([indeterminate]) .bar-value {
          width: 7.5rem !important;
          animation: indeterminate 2s linear infinite;
        }
        @keyframes indeterminate {
          from {
            left: -7.5rem;
          }
          to {
            left: 100%;
          }
        }
      `,
    ];
  }

  render() {
    return html`
      <!-- header -->
      ${this.header || this.label
        ? html`
            <div class="header-wrapper">
              ${this.header && this.size !== 'xs'
                ? html` <sh-text size="header-2" class="header">${this.header}</sh-text> `
                : ''}
              ${this.label && !this.radial
                ? html` <sh-text size="title-1" class="label">${this.label}</sh-text> `
                : ''}
            </div>
          `
        : ''}
      ${!this.radial
        ? html`
            <!-- linear -->
            <div class="bar-background">
              <div
                class="bar-value"
                style="width: ${this.value}%; background: ${this.paused
                  ? 'rgba(var(--ui-1), var(--opacity-3))'
                  : this.color
                  ? this.color
                  : 'rgb(var(--ui-2))'}"
              ></div>
            </div>
          `
        : html`
            <!-- radial -->
            <div class="radial-wrapper">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="radial"
                .style="width: ${this.getSize() / 16}rem; height: ${this.getSize() / 16}rem"
              >
                <circle
                  stroke="rgba(var(--ui-1), var(--opacity-6))"
                  .style="
                    r: ${(this.getSize() / 2 -
                    4 -
                    (this.size === 's'
                      ? -1
                      : this.size === 'l'
                      ? 1
                      : this.size === 'xl'
                      ? 2
                      : this.size === 'xs'
                      ? -2
                      : 0)) /
                  16}rem;
                    cx: ${this.getSize() / 2 / 16}rem;
                    cy: ${this.getSize() / 2 / 16}rem;
                  "
                />
                <circle
                  class="bar-highlight"
                  stroke="${this.color ? this.color : 'rgb(var(--ui-2))'}"
                  .style="
                    stroke-dasharray: ${2 *
                  Math.PI *
                  (this.getSize() / 2 - (this.size === 'xs' ? 2 : this.size === 's' ? 3 : 4)) *
                  this.getFontScale()};
                    stroke-dashoffset: ${2 *
                  Math.PI *
                  (this.getSize() / 2 - (this.size === 'xs' ? 2 : this.size === 's' ? 3 : 4)) *
                  (1 - this.value / 100) *
                  this.getFontScale()};
                    r: ${(this.getSize() / 2 -
                    4 -
                    (this.size === 's'
                      ? -1
                      : this.size === 'l'
                      ? 1
                      : this.size === 'xl'
                      ? 2
                      : this.size === 'xs'
                      ? -2
                      : 0)) /
                  16}rem;
                    cx: ${this.getSize() / 2 / 16}rem;
                    cy: ${this.getSize() / 2 / 16}rem;
                  "
                />
              </svg>
              ${this.label && this.size !== 's' && this.size !== 'xs'
                ? html` <sh-text size="title-1" class="radial-label">${this.label}</sh-text> `
                : ''}
            </div>
          `}
      ${this.info || this.status
        ? html`
            <div class="footer">
              <!-- status -->
              ${this.status
                ? html` <sh-icon size="xs" class="status-icon" icon="${this.status}"></sh-icon> `
                : ''}
              <!-- info -->
              ${this.info && this.size !== 'xs'
                ? html`
                    <sh-text size="body-1" color="secondary" class="info">${this.info}</sh-text>
                  `
                : ''}
              <slot name="functions"></slot>
            </div>
          `
        : ''}
    `;
  }

  attributeChangedCallback(name, oldval, newval) {
    super.attributeChangedCallback(name, oldval, newval);
    // handle legacy 'type' property
    if (name === 'type' && this.type) {
      this.status = this.type;
      console.warn(
        `You are using a legacy property ('type') of sh-progress component. Please use the new 'status' property instead.`
      );
    }
  }

  constructor() {
    super();
    this.value = 0;
    this.size = 'm';
  }

  updated(changedProperties) {
    const listOfProperties = [
      'header',
      'label',
      'info',
      'status',
      'color',
      'size',
      'value',
      'indeterminate',
      'paused',
      'radial',
      'type',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'progressbar');
    }
  }

  getSize() {
    let size;
    switch (this.size) {
      case 'xs':
        size = 16;
        break;
      case 's':
        size = 32;
        break;
      case 'm':
        size = 64;
        break;
      case 'l':
        size = 128;
        break;
      case 'xl':
        size = 256;
        break;
    }
    return size;
  }

  getFontScale() {
    const computedSize = window.getComputedStyle(document.documentElement).fontSize;
    return computedSize.substring(0, computedSize.length - 2) / 16;
  }
}

customElements.define('sh-progress', SHProgress);
